'use client';

import * as React from 'react';
import { cn } from './cn';
import { useControllableState, useElementDirection, useId } from './hooks';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  /** Required when the option renders as icon-only. */
  ariaLabel?: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  options: SegmentedOption<T>[];
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  size?: 'sm' | 'md';
  /** Split the width evenly — the usual choice inside a sheet. */
  fullWidth?: boolean;
  'aria-label'?: string;
}

/**
 * Mutually-exclusive choice among 2–5 short options (align start/center/end,
 * page mode, cover placement). Implemented as a radiogroup, so arrow keys move
 * *and* select, mirrored for RTL.
 */
export function SegmentedControl<T extends string = string>({
  options,
  value,
  defaultValue,
  onValueChange,
  size = 'md',
  fullWidth,
  className,
  ...props
}: SegmentedControlProps<T>) {
  const firstValue = options[0]?.value as T;
  const [current, setCurrent] = useControllableState<T>(
    value,
    (defaultValue ?? firstValue) as T,
    onValueChange,
  );
  const ref = React.useRef<HTMLDivElement>(null);
  const dir = useElementDirection(ref);
  const name = useId('segmented');

  const move = (from: number, delta: number) => {
    const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
    const pos = enabled.indexOf(from);
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    if (next === undefined) return;
    const opt = options[next];
    if (!opt) return;
    setCurrent(opt.value);
    ref.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
    const fwd = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === fwd || e.key === 'ArrowDown') { e.preventDefault(); move(i, 1); }
    else if (e.key === back || e.key === 'ArrowUp') { e.preventDefault(); move(i, -1); }
  };

  return (
    <div
      ref={ref}
      role="radiogroup"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg bg-paper-3 p-0.5',
        fullWidth && 'flex w-full',
        className,
      )}
      {...props}
    >
      {options.map((o, i) => {
        const selected = o.value === current;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            name={name}
            aria-checked={selected}
            aria-label={o.ariaLabel ?? (typeof o.label === 'string' ? o.label : undefined)}
            tabIndex={selected ? 0 : -1}
            disabled={o.disabled}
            data-state={selected ? 'active' : 'inactive'}
            onClick={() => setCurrent(o.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-md',
              'font-medium whitespace-nowrap transition-all duration-(--duration-fast) ease-out-soft',
              'disabled:pointer-events-none disabled:opacity-45',
              size === 'sm' ? 'k-hit h-8 px-2.5 text-xs [&_svg]:size-4' : 'h-10 px-3 text-sm [&_svg]:size-[1.125rem]',
              fullWidth && 'flex-1',
              selected
                ? 'bg-surface-2 text-ink shadow-xs'
                : 'text-ink-3 hover:text-ink-2',
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
